import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostBinding, HostListener, inject, input, output, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NotesActions } from '@src/suite/common/store/notes/notes.actions';
import { toLuxonRelativeDateString } from '@src/suite/common/utils/date-utils';
import { ExternalActionsOverlayComponent } from '@src/suite/playbook/components/external-actions-overlay/external-actions-overlay';

import { StTextAreaOverlayComponent } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StatusComponent } from '../../controls/status/status';
import { CardLeftBorderDirective } from '../../directives/card-left-border';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { NotesRecordActions } from '../../store/records/records.actions';
import { I18nKeys } from '../../utils/i18n.default';

@Component({
    selector: 'st-note-item',
    imports: [
        CardLeftBorderDirective,
        ClickStopPropagationDirective,
        ExternalActionsOverlayComponent,
        MatButtonModule,
        MatIcon,
        MatTooltipModule,
        NgTemplateOutlet,
        StatusComponent,
        StTextAreaOverlayComponent,
    ],
    templateUrl: './note-item.component.html',
    styleUrl: './note-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-note-item',
    },
})
export class NoteItemComponent {
    // injections
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    appService = inject(AppService);

    // store signals
    languageSettings = this.store.selectSignal(selectI18N)();

    //Enums and constants
    pstFieldNames = PSTFieldNames;

    // inputs
    readonly searchTerm = input<string>(null);
    readonly expanded = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    readonly note = input<StorytellingNoteRecord>(null);

    // outputs
    readonly itemClicked = output<void>();

    // computed
    readonly timeUpdatedLabel = computed((): string => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedAgoLabel))()} ${toLuxonRelativeDateString(this.note()?.ModTime, this.appService.updatedAgoSignal())} `;
    });

    readonly updatedBy = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.note()?.ModBy]?.DisplayName}`;
    });

    readonly expandNoteLabel = computed(() => (this.expanded() ? 'keyboard_arrow_up' : 'keyboard_arrow_down'));

    readonly textMaxRows = computed(() => (this.expanded() ? 3 : 1));

    @HostBinding('style.height')
    get hostHeight(): string {
        return this.expanded() ? '7rem' : '3.5rem';
    }

    @HostListener('click')
    public onClick() {
        this.itemClicked.emit();
    }

    changeNoteStatus(update: { id: string; value: any }) {
        this.store.dispatch(
            NotesActions.updateCurrentNote({
                record: this.note(),
                fieldsToUpdate: [{ key: PSTFieldNames.PSTNoteStatus, value: update }],
            })
        );
    }

    toggleExpandNote() {
        this.store.dispatch(NotesRecordActions.changeNoteExpandedState({ guid: this.note()?.Guid, isExpanded: !this.expanded() }));
    }
}
